/* @flow */

export type QueryValue = string | Array<?string> | null;
export type QueryObject = { [string]: QueryValue };
import type { FormData } from "@awardit/formaggio/src/types";

import React, { useState, useContext, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useData, useSendMessage } from "crustate/react";
import { useTranslate } from "@awardit/react-use-translate";
import { useClient, StoreInfoContext } from "entrypoint/shared";
import { RegisterFormConfigData, DistrictListData } from "data";
import RegisterForm from "./form";
import { createArrayFromObj, formatRegisterFormRequest } from "helpers/forms";
import { addMessageTranslated } from "state/messages";
import Button from "components/Button";
import { registerMember } from "queries";

import cn from "classnames";
import styles from "./styles.scss";

type Props = {
  queryParams: QueryObject,
  partnerId: number,
  routeParams?: { [string]: string },
};

const RegisterView = ({ queryParams, partnerId }: Props): React$Node => {
  const client = useClient();
  const t = useTranslate();
  const sendMessage = useSendMessage();
  const {
    routes: {
      registerView,
      loginView,
    },
    content: { registerform } } = useContext(StoreInfoContext);
  const [formValue, setFormValue] = useState<FormData>({});
  const [error, setError] = useState(null);
  const [genderValue, setGenderValue] = useState<string>("");
  const [registered, setRegistered] = useState(false);
  const formConfigData = useData(RegisterFormConfigData);
  const districtListData = useData(DistrictListData);
  const formConfigContent = formConfigData.state === "LOADED" ? formConfigData.data : null;
  const districtListArr = districtListData.state === "LOADED" ? districtListData.data.getDistrictList : null;

  useEffect(() => {
    if (error && error.length > 0) {
      sendMessage(addMessageTranslated(error, "error"));
      window.scrollTo(0, 0);
    }
  }, [error]);

  if ((registerView && registerView.toggle === false) || !formConfigContent) {
    return null;
  }

  const formArr = createArrayFromObj(formConfigContent);

  const postForm = async () => {
    const data = formatRegisterFormRequest(formValue);

    if (genderValue !== "") {
      data.gender = genderValue;
    }

    const response = await client(registerMember, { partner: partnerId, member: {
      ...data,
    } });

    if (response.registerMember.result.includes("ERROR")) {
      if (response.registerMember.message) {
        setError(response.registerMember.message);
      }

      return;
    }

    setRegistered(true);
    window.scrollTo(0, 0);
  };

  return (
    <div className={cn(styles.registerView, "awardit-registerView")}>
      <Helmet title={(registerView && registerView.title) ?? ""} />
      {registered ? (
        <div className={cn(styles.successBox, "awardit-registerSuccessBox")}>
          <h1 className={styles.successHeading}>{registerform.successTitle ?? t("REGISTER.SUCCESS.TITLE")}</h1>
          {registerform.successText &&
            <p className={styles.successText}>{registerform.successText}</p>
          }
          <Button size="small" variant="primary" className={styles.successLink} to={loginView?.url ?? "/login"}>
            {t("REGISTER.SUCCESS.LINK_TEXT")}
          </Button>
        </div>
      ) : (
        <RegisterForm
          formArr={formArr}
          value={formValue}
          setValue={x => setFormValue(x)}
          postForm={postForm}
          partnerId={partnerId}
          queryParams={queryParams}
          genderValue={genderValue}
          setGenderValue={setGenderValue}
          districtList={districtListArr}
        />
      )}
    </div>
  );
};

export default RegisterView;
